import { useRef } from 'react';
import { Box, TextField, IconButton, InputAdornment } from '@mui/material';
import CalendarTodayRounded from '@mui/icons-material/CalendarTodayRounded';
import { formatDateOnly } from '@dosfilos/shared';
import { useTenantDateFormat } from '../stores/tenant.store';

/**
 * A date-only picker that DISPLAYS the day in the tenant's `dateFormat`
 * (dd-mm / mm-dd / yyyy-mm), unlike the native `<input type="date">` whose field
 * order follows the browser locale. It keeps the native calendar for picking (via
 * `showPicker()` on a hidden input) but shows the chosen day formatted per the tenant.
 *
 * The value contract matches the native input — a `yyyy-MM-dd` calendar day (or '')
 * — so callers and the server are unchanged; only the display differs.
 */
export interface TenantDateFieldProps {
  label?: string;
  /** `yyyy-MM-dd` or ''. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  /** Calendar bounds (`yyyy-MM-dd`). */
  min?: string;
  max?: string;
}

export function TenantDateField({
  label,
  value,
  onChange,
  disabled = false,
  size = 'small',
  fullWidth = false,
  error = false,
  helperText,
  min,
  max,
}: TenantDateFieldProps): JSX.Element {
  const dateFormat = useTenantDateFormat();
  const ref = useRef<HTMLInputElement>(null);

  const open = () => {
    if (disabled) return;
    const el = ref.current;
    if (!el) return;
    // showPicker() is the modern way to open the native calendar from a gesture,
    // but it throws NotAllowedError without transient user activation (e.g. a
    // programmatic/synthetic click). Guard it and fall back to focus.
    try {
      if (typeof el.showPicker === 'function') el.showPicker();
      else el.focus();
    } catch {
      el.focus();
    }
  };

  const display = value ? formatDateOnly(value, dateFormat) : '';

  return (
    <Box
      sx={{
        position: 'relative',
        display: fullWidth ? 'block' : 'inline-block',
        ...(fullWidth ? { width: '100%' } : {}),
      }}
    >
      <TextField
        value={display}
        placeholder={dateFormat}
        onClick={open}
        disabled={disabled}
        size={size}
        fullWidth={fullWidth}
        error={error}
        InputLabelProps={{ shrink: true }}
        {...(label !== undefined ? { label } : {})}
        {...(helperText !== undefined ? { helperText } : {})}
        InputProps={{
          readOnly: true,
          sx: { cursor: disabled ? 'default' : 'pointer' },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton edge="end" size="small" onClick={open} disabled={disabled} tabIndex={-1}>
                <CalendarTodayRounded fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {/* Hidden native input: hosts the calendar popup (anchored here) + owns the value. */}
      <input
        ref={ref}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden
        {...(min ? { min } : {})}
        {...(max ? { max } : {})}
        style={{
          position: 'absolute',
          left: 12,
          bottom: 0,
          width: 1,
          height: 1,
          padding: 0,
          border: 0,
          opacity: 0,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}
